// src/hooks/useAuth.js
import { useState, useEffect, createContext, useContext, useRef } from 'react';
import { AuthContext } from './AuthContext.js';
import apiService from '../services/api';

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // Initialize from localStorage if available
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('userData');
      if (stored) {
        const userData = JSON.parse(stored);
        // Normalize user data: add 'role' property based on roles array
        return {
          ...userData,
          role: userData.role || (Array.isArray(userData.roles) && userData.roles[0]) || 'customer'
        };
      }
      return null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const checkAuthRunning = useRef(false); // Prevent multiple concurrent calls

  useEffect(() => {
    if (!checkAuthRunning.current) {
      checkAuthRunning.current = true;
      checkAuth();
    }
  }, []);

  // Check if user is authenticated
  const checkAuth = async () => {
    // If userData is already available in state or localStorage, skip the profile check
    // This prevents clearing the token immediately after login
    if (!apiService.isLoggedIn()) {
      console.log('👤 useAuth: No token in localStorage, skipping checkAuth');
      setLoading(false);
      return;
    }

    // If userData is in localStorage, use it and DON'T call getProfile()
    // This prevents unnecessary API calls that could fail and clear the token
    try {
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        const normalizedUser = {
          ...userData,
          role: userData.role || (Array.isArray(userData.roles) && userData.roles[0]) || 'customer'
        };
        console.log('👤 useAuth: Loading user from localStorage:', normalizedUser.email);
        setUser(normalizedUser);
        setLoading(false);
        return;
      }
    } catch (e) {
      console.error('👤 useAuth: Failed to parse userData from localStorage:', e);
    }

    // If user is already set in state, we don't need to verify again
    if (user) {
      console.log('👤 useAuth: User already in state, skipping profile verification');
      setLoading(false);
      return;
    }

    // Only try to fetch profile as a last resort
    // Try to fetch profile to verify token is still valid
    try {
      console.log('👤 useAuth: Verifying token with profile check...');
      const response = await apiService.getProfile();
      if (response.success && response.data) {
        // Normalize user data: add 'role' property based on roles array
        const normalizedUser = {
          ...response.data,
          role: response.data.role || (Array.isArray(response.data.roles) && response.data.roles[0]) || 'customer'
        };
        console.log('👤 useAuth: checkAuth verified user:', normalizedUser.email, 'roles:', normalizedUser.roles, 'primaryRole:', normalizedUser.role);
        setUser(normalizedUser);
      } else {
        // If profile fetch fails, clear token and user
        console.error('👤 useAuth: Profile fetch failed, clearing token:', response);
        apiService.clearToken();
        setUser(null);
      }
    } catch (error) {
      console.error('👤 useAuth: Auth check failed:', error.message);
      // Only clear token on 401 or actual authentication errors
      // Don't clear on network errors - let the user stay logged in
      if (error.message?.includes('Unauthorized')) {
        console.warn('👤 useAuth: Token is unauthorized, clearing it');
        apiService.clearToken();
        setUser(null);
      } else {
        console.warn('👤 useAuth: Network error during profile check, keeping session alive');
      }
    }
    setLoading(false);
  };

  // Register user
  const register = async (userData) => {
    try {
      const response = await apiService.register(userData);
      
      if (response.success && response.data?.user) {
        // Normalize user data: add 'role' property based on roles array
        const normalizedUser = {
          ...response.data.user,
          role: response.data.user.role || (Array.isArray(response.data.user.roles) && response.data.user.roles[0]) || 'customer'
        };
        // Store token in localStorage directly (bypass apiService)
localStorage.setItem('token', response.data.token);
console.log('🔐 ProviderLoginPage: Token stored in localStorage directly');
        setUser(normalizedUser);
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Login user
  const login = async (credentials) => {
    try {
      console.log('useAuth login called with:', credentials);
      const response = await apiService.login(credentials);
      console.log('API response:', response);
      
      if (response.success && response.data?.user) {
        // Normalize user data: add 'role' property based on roles array
        const normalizedUser = {
          ...response.data.user,
          role: response.data.user.role || (Array.isArray(response.data.user.roles) && response.data.user.roles[0]) || 'customer'
        };
        console.log('Setting user:', normalizedUser);
        // Store user data in localStorage for persistence across page reloads
        localStorage.setItem('userData', JSON.stringify(normalizedUser));
        setUser(normalizedUser);
        console.log('User set successfully and stored in localStorage');
      }
      
      return response;
    } catch (error) {
      console.error('useAuth login error:', error);
      throw error;
    }
  };

  // Logout user
  const logout = () => {
    apiService.clearToken();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userData');
    setUser(null);
  };

  // Wrapper to log setUser calls and normalize role data
  const setUserLogged = (userData) => {
    if (userData) {
      // Normalize user data: add 'role' property based on roles array
      const normalizedUser = {
        ...userData,
        role: userData.role || (Array.isArray(userData.roles) && userData.roles[0]) || 'customer'
      };
      console.log('👤 useAuth: setUser called with:', normalizedUser?.email, 'roles:', normalizedUser?.roles, 'primaryRole:', normalizedUser?.role);
      setUser(normalizedUser);
    } else {
      console.log('👤 useAuth: setUser called with null');
      setUser(null);
    }
    
    // Persist to localStorage so it survives navigation
    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
      console.log('👤 useAuth: User persisted to localStorage');
    } else {
      localStorage.removeItem('userData');
      console.log('👤 useAuth: User cleared from localStorage');
    }
    
    // Mark loading as complete when user is set
    if (userData) {
      console.log('👤 useAuth: User set, marking auth as loaded');
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        register,
        login,
        logout,
        setUser: setUserLogged,
        isAuthenticated: !!user
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};